/**
 * DateTile — weekday + day + month + ISO week number.
 *
 * Plan 01-14 Task 1.  Fills the `data-tile="date"` slot in IdleMode.
 *
 * Uses `formatDate` (Plan 10 nb-NO Intl formatter: "onsdag 22. april") and
 * `formatWeekNumber` (date-fns getISOWeek, Monday-anchored per FND-08).
 * The 'uke' label + '·' separator live in nb.json under
 * `dashboard.date.composed` so no Norwegian prose is inlined in JSX
 * (MED-7 / jsx-no-literals).
 *
 * Refresh: schedules a single setTimeout to next midnight so the date flips
 * on rollover without paying for a minute-tick re-render.  The tile is only
 * mounted inside IdleMode (which is always-mounted on /), so the midnight
 * refresh is the only tick that matters.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate, formatWeekNumber } from '../../../i18n/formatters';

function msUntilNextMidnight(): number {
  const d = new Date();
  d.setHours(24, 0, 0, 0);
  return d.getTime() - Date.now();
}

/**
 * Dagens dato, som byttes ved midnatt. Delt med toppen og «I dag»-flisen på
 * Hjem (#154), så de også bytter dag uten å tikke hvert minutt.
 */
export function useToday(): Date {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timeout = setTimeout(() => setNow(new Date()), msUntilNextMidnight());
    return () => clearTimeout(timeout);
  }, [now]);

  return now;
}

export function DateTile() {
  const { t } = useTranslation();
  const now = useToday();

  const composed = t('dashboard.date.composed', {
    date: formatDate(now),
    week: formatWeekNumber(now),
  });

  return (
    <div
      data-testid="date-tile"
      // Stell_Kjokken (#160): «Lørdag 24. oktober · uke 43», stor forbokstav.
      className="mt-2.5 text-heading-m font-semibold text-ink-2 first-letter:uppercase"
    >
      {composed}
    </div>
  );
}
